// src/theme/contrast.ts
function hexToRgb(hex) {
  let h = hex.trim();
  if (h.startsWith("#")) h = h.slice(1);
  if (h.length === 3) {
    h = h.split("").map((c) => c + c).join("");
  }
  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) {
    throw new Error(`hexToRgb: invalid hex color "${hex}"`);
  }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  };
}
function rgbToHex({ r, g, b }) {
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
  const hex = (n) => clamp(n).toString(16).padStart(2, "0");
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}
function hexToHsl(hex) {
  const { r, g, b } = hexToRgb(hex);
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const delta = max - min;
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    if (max === rN) {
      h = (gN - bN) / delta + (gN < bN ? 6 : 0);
    } else if (max === gN) {
      h = (bN - rN) / delta + 2;
    } else {
      h = (rN - gN) / delta + 4;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}
function hslToHex({ h, s, l }) {
  const hN = (h % 360 + 360) % 360 / 360;
  const sN = Math.max(0, Math.min(100, s)) / 100;
  const lN = Math.max(0, Math.min(100, l)) / 100;
  if (sN === 0) {
    const gray = lN * 255;
    return rgbToHex({ r: gray, g: gray, b: gray });
  }
  const q = lN < 0.5 ? lN * (1 + sN) : lN + sN - lN * sN;
  const p = 2 * lN - q;
  const hue2rgb = (t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const r = hue2rgb(hN + 1 / 3) * 255;
  const g = hue2rgb(hN) * 255;
  const b = hue2rgb(hN - 1 / 3) * 255;
  return rgbToHex({ r, g, b });
}
function relativeLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
function contrastRatio(hex1, hex2) {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}
var DARK_PRIMARY_FALLBACK = "#C97AA0";
function deriveDarkPrimary(primaryHex, darkBgHex = "#1A202C", targetRatio = 4.5) {
  if (contrastRatio(primaryHex, darkBgHex) >= targetRatio) {
    return primaryHex;
  }
  const hsl = hexToHsl(primaryHex);
  for (let l = Math.ceil(hsl.l) + 1; l <= 100; l++) {
    const candidate = hslToHex({ h: hsl.h, s: hsl.s, l });
    if (contrastRatio(candidate, darkBgHex) >= targetRatio) {
      return candidate;
    }
  }
  if (typeof console !== "undefined" && console.warn) {
    console.warn(
      `[deriveDarkPrimary] Could not derive a dark-safe shade of "${primaryHex}" against "${darkBgHex}" at target ${targetRatio}:1. Falling back to ${DARK_PRIMARY_FALLBACK}.`
    );
  }
  return DARK_PRIMARY_FALLBACK;
}

// src/theme/editorial.ts
var SHADOW_INK = "62, 23, 41";
var SECONDARY = {
  lighter: "#F6EDD0",
  light: "#E7C87B",
  main: "#DAA03D",
  dark: "#86601D",
  darker: "#6E4F18",
  // text on gold-lighter
  contrastText: "#2B1B23"
};
var SUCCESS = {
  lighter: "#DDF3E5",
  light: "#6AD89C",
  main: "#2E9A5E",
  dark: "#1B6B40",
  darker: "#136643",
  contrastText: "#FFFFFF"
};
var WARNING = {
  lighter: "#F6EDD0",
  light: "#E7C87B",
  main: "#DAA03D",
  dark: "#B07A1E",
  darker: "#6E4F18",
  contrastText: "#2B1B23"
};
var ERROR = {
  lighter: "#FFE7D9",
  light: "#F2C9B8",
  main: "#D14343",
  dark: "#B72136",
  darker: "#7A0C2E",
  contrastText: "#FFFFFF"
};
var GREY = {
  100: "#FBF8F4",
  200: "#F4EDE4",
  300: "#EFE7DE",
  400: "#E6DCD2",
  500: "#D6C6B8",
  600: "#9A8A92",
  700: "#6B5A62",
  800: "#4E3A44",
  900: "#2B1B23"
};
var SIDEBAR = {
  bg: "#3E1729",
  fg: "#FAF6F0",
  fgMuted: "#D8C6CF",
  itemHoverBg: "rgba(250, 246, 240, 0.06)",
  itemSelectedBg: "rgba(250, 246, 240, 0.10)",
  accent: "#DAA03D",
  divider: "rgba(250, 246, 240, 0.12)"
};
var LIGHT = {
  primary: {
    lighter: "#E7D6E0",
    light: "#C87DA1",
    main: "#9A3F69",
    dark: "#702C4D",
    darker: "#3E1729",
    // aubergine: sections, sidebar, dark cards
    contrastText: "#FFFFFF"
  },
  secondary: SECONDARY,
  success: SUCCESS,
  info: {
    lighter: "#E7D6E0",
    light: "#C87DA1",
    main: "#9A3F69",
    dark: "#6B2548",
    darker: "#4A1C33",
    contrastText: "#FFFFFF"
  },
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  background: {
    default: "#FAF6F0",
    // the slide background (cream)
    paper: "#FFFFFF",
    neutral: "#F4EDE4",
    // table heads, toolbars, soft surfaces
    subtle: "#FBF8F4",
    inverse: "#3E1729",
    device: "#1E1418"
  },
  text: {
    primary: "#2B1B23",
    secondary: "#6B5A62",
    tertiary: "#4E3A44",
    disabled: "#9A8A92",
    onInverse: "#FAF6F0",
    onInverseMuted: "#D8C6CF"
  },
  divider: "#E6DCD2",
  dividerSoft: "#EFE7DE",
  action: {
    hover: "rgba(154, 63, 105, 0.06)",
    selected: "rgba(154, 63, 105, 0.12)",
    focus: "rgba(154, 63, 105, 0.20)"
  },
  status: {
    available: { bg: "#DDF3E5", fg: "#1B6B40", border: "#CFE9D8" },
    // Beschikbaar / OK
    rollout: { bg: "#F6EDD0", fg: "#6E4F18", border: "#EEDDB0" },
    // In uitrol / Overgeslagen
    urgent: { bg: "#FFE7D9", fg: "#7A0C2E", border: "#F2C9B8" },
    // Dringend / Geweigerd
    brand: { bg: "#E7D6E0", fg: "#6B2548", border: "#C87DA1" },
    // Intern / geselecteerd
    neutral: { bg: "#F4EDE4", fg: "#4E3A44", border: "#E6DCD2" }
    // Wachtend / tags
  },
  sidebar: SIDEBAR
};
var DARK_PRIMARY = {
  lighter: "#F3E9EF",
  light: "#E7D6E0",
  main: "#C87DA1",
  dark: "#9A3F69",
  darker: "#702C4D",
  contrastText: "#1A1016"
};
var DARK = {
  primary: DARK_PRIMARY,
  secondary: SECONDARY,
  success: SUCCESS,
  info: DARK_PRIMARY,
  // info = plum, as in light
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  background: {
    default: "#1A1016",
    paper: "#2E1B24",
    neutral: "#3A2530",
    subtle: "#22151C",
    inverse: "#3E1729",
    device: "#1E1418"
  },
  text: {
    primary: "#FAF6F0",
    secondary: "#D8C6CF",
    tertiary: "#E7D6E0",
    disabled: "#9A8A92",
    onInverse: "#FAF6F0",
    onInverseMuted: "#D8C6CF"
  },
  divider: "rgba(250, 246, 240, 0.12)",
  dividerSoft: "rgba(250, 246, 240, 0.08)",
  action: {
    hover: "rgba(200, 125, 161, 0.08)",
    selected: "rgba(200, 125, 161, 0.16)",
    focus: "rgba(200, 125, 161, 0.24)"
  },
  // bg = 16 % of the status main colour on the dark paper `#2E1B24`, border = 40 %, fg = the
  // light variant of the colour; neutral uses the dark neutral surface. Solid hex on purpose:
  // "never transparent" holds in dark too.
  status: {
    available: { bg: "#2E2F2D", fg: "#6AD89C", border: "#2E4E3B" },
    rollout: { bg: "#4A3028", fg: "#E7C87B", border: "#73502E" },
    urgent: { bg: "#482129", fg: "#F2C9B8", border: "#6F2B30" },
    brand: { bg: "#3F212F", fg: "#E7D6E0", border: "#592940" },
    neutral: { bg: "#3A2530", fg: "#D8C6CF", border: "#513E47" }
  },
  sidebar: SIDEBAR
};
var CARO_EDITORIAL_TOKENS = {
  light: LIGHT,
  dark: DARK,
  typography: {
    fontFamily: '"Nunito Variable", Nunito, system-ui, sans-serif',
    fontFamilyDisplay: '"Newsreader Variable", Newsreader, Georgia, serif',
    // headings, big numbers
    fontFamilyMono: '"JetBrains Mono", ui-monospace, monospace',
    // HEX ids, codes
    headingWeight: 500,
    headingLineHeight: 1.1,
    headingLetterSpacing: "-0.005em",
    overline: { fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase" }
  },
  radius: { xs: 6, sm: 10, md: 12, lg: 18, xl: 22, device: 44, pill: 999 },
  shadowInk: SHADOW_INK,
  customShadows: {
    card: `0 1px 2px rgba(${SHADOW_INK}, 0.04), 0 12px 32px -16px rgba(${SHADOW_INK}, 0.22)`,
    cardHover: `0 30px 60px -30px rgba(${SHADOW_INK}, 0.35)`,
    dropdown: `0 6px 18px -8px rgba(${SHADOW_INK}, 0.20), 0 20px 40px -20px rgba(${SHADOW_INK}, 0.25)`,
    button: `0 6px 18px -8px rgba(${SHADOW_INK}, 0.35)`,
    dialog: `0 60px 120px -40px rgba(${SHADOW_INK}, 0.45), 0 8px 24px -8px rgba(${SHADOW_INK}, 0.15)`,
    primary: "0 12px 28px -12px rgba(154, 63, 105, 0.55)",
    hero: `0 50px 100px -40px rgba(${SHADOW_INK}, 0.35)`
  },
  gradients: {
    primary: "linear-gradient(150deg, #B24D7B 0%, #8A3459 100%)",
    secondary: "linear-gradient(135deg, #DAA03D 0%, #E7C87B 100%)",
    inverse: "radial-gradient(ellipse at 50% 40%, #6A2F4B 0%, #3E1729 62%, #2A0F1C 100%)",
    success: "linear-gradient(135deg, #2E9A5E 0%, #6AD89C 100%)",
    warning: "linear-gradient(135deg, #DAA03D 0%, #E7C87B 100%)",
    error: "linear-gradient(135deg, #B72136 0%, #D14343 100%)",
    dark: "linear-gradient(135deg, #2B1B23 0%, #4E3A44 100%)"
  }
};
function pairsFor(mode) {
  const t = CARO_EDITORIAL_TOKENS[mode];
  const p = (label, fg, bg, min = 4.5, over) => ({ mode, label, fg, bg, min, ...over ? { over } : {} });
  return [
    p("text.primary on background.default", t.text.primary, t.background.default),
    p("text.primary on background.paper", t.text.primary, t.background.paper),
    p("text.primary on background.neutral", t.text.primary, t.background.neutral),
    p("text.primary on background.subtle", t.text.primary, t.background.subtle),
    p("text.secondary on background.default", t.text.secondary, t.background.default),
    p("text.secondary on background.paper", t.text.secondary, t.background.paper),
    p("text.secondary on background.neutral", t.text.secondary, t.background.neutral),
    p("text.tertiary on background.paper", t.text.tertiary, t.background.paper),
    p("text.onInverse on background.inverse", t.text.onInverse, t.background.inverse),
    p("text.onInverseMuted on background.inverse", t.text.onInverseMuted, t.background.inverse),
    p("primary.main on background.paper", t.primary.main, t.background.paper),
    p("primary.main on background.default", t.primary.main, t.background.default),
    p("primary.contrastText on primary.main", t.primary.contrastText, t.primary.main),
    p("secondary.contrastText on secondary.main", t.secondary.contrastText, t.secondary.main),
    p("sidebar.fg on sidebar.bg", t.sidebar.fg, t.sidebar.bg),
    p("sidebar.fgMuted on sidebar.bg", t.sidebar.fgMuted, t.sidebar.bg),
    p("sidebar.accent on sidebar.bg", t.sidebar.accent, t.sidebar.bg),
    p("sidebar.fg on sidebar.itemSelectedBg", t.sidebar.fg, t.sidebar.itemSelectedBg, 4.5, t.sidebar.bg),
    p("sidebar.fg on sidebar.itemHoverBg", t.sidebar.fg, t.sidebar.itemHoverBg, 4.5, t.sidebar.bg),
    p("status.available", t.status.available.fg, t.status.available.bg),
    p("status.rollout", t.status.rollout.fg, t.status.rollout.bg),
    p("status.urgent", t.status.urgent.fg, t.status.urgent.bg),
    p("status.brand", t.status.brand.fg, t.status.brand.bg),
    p("status.neutral", t.status.neutral.fg, t.status.neutral.bg),
    // LD-14: filled success buttons sit on success.dark; white on success.main is 3.56:1.
    p("button text on success.dark", t.success.contrastText, t.success.dark),
    p("disabled: text.disabled on background.paper", t.text.disabled, t.background.paper, 3)
  ];
}
var CARO_EDITORIAL_CONTRAST_PAIRS = [
  ...pairsFor("light"),
  ...pairsFor("dark")
];

// src/theme/catalog.ts
var BUNDLED_FONTS = ["system", "nunito", "newsreader", "jetbrains-mono"];
var RESIDENT_THEME_CHROME_VARS = [
  // T095
  "--caro-skeleton-bg",
  "--caro-control-bg",
  "--caro-control-bg-disabled",
  "--caro-control-fg",
  "--caro-control-fg-disabled",
  // Surfaces and text
  "--caro-paper",
  // a card painted with a white literal (was 'white' / '#FFFFFF')
  "--caro-surface-muted",
  // soft neutral surface: hover, inactive pill, secondary button
  "--caro-surface-subtle",
  // quiet surface inside a card
  "--caro-text-strong",
  // dark secondary text (was #2D3748 / #4A5568)
  "--caro-text-subtle",
  // hints and placeholders (was #A0AEC0 / #999)
  "--caro-divider",
  // hairline divider (was #E2E8F0 as a literal)
  "--caro-divider-strong",
  // stronger divider / disabled fill (was #CBD5E0)
  "--caro-on-primary",
  // text and icons on a filled primary surface (was white)
  // Depth
  "--caro-card-shadow",
  "--caro-raised-shadow",
  "--caro-backdrop",
  // modal backdrop (was rgba(0,0,0,0.45..0.55))
  "--caro-inverse-bg",
  // dark utility button (was navy #1A202C)
  // Accents
  "--caro-accent",
  // gold
  "--caro-accent-soft",
  "--caro-accent-fg",
  "--caro-primary-soft",
  "--caro-primary-soft-fg",
  "--caro-title-rule",
  // box-shadow under page titles (a gold rule, no layout change)
  "--caro-card-accent",
  // the coloured left edge of a highlighted card (announcement)
  // Page chrome (back button) and the Home glass tiles
  "--caro-nav-bg",
  "--caro-nav-fg",
  "--caro-nav-fg-muted",
  "--caro-nav-border",
  // A page header that sits inside the 24px page padding: painted as a full-bleed band with an
  // outer box-shadow (spread 24px, shifted up 16px: 40px above, 8px below, 24px each side), so
  // it needs no layout change and caro-default stays pixel-identical.
  "--caro-header-band",
  "--caro-home-tile-tint",
  "--caro-home-tile-border",
  "--caro-home-scrim",
  // over the stock Home background (no resident photos)
  "--caro-home-scrim-photo",
  // over resident photos: keeps them visible, text readable
  "--caro-home-caption-bg",
  "--caro-standby-bg-dark",
  // screensaver/sleep without photos, "donker"
  "--caro-standby-bg-light",
  // idem, "wit"
  "--caro-call-bg",
  // The system font, for screens that must look the same under every theme (noodknop, alarm,
  // emergency call). Equal to what --caro-font paints without a theme.
  "--caro-font-system",
  // a normal (non-emergency) call screen; emergency keeps --caro-emergency
  // Informational status pairs (chips/badges -- never an alarm state)
  "--caro-status-available-bg",
  "--caro-status-available-fg",
  "--caro-status-available-border",
  "--caro-status-rollout-bg",
  "--caro-status-rollout-fg",
  "--caro-status-rollout-border",
  "--caro-status-urgent-bg",
  "--caro-status-urgent-fg",
  "--caro-status-urgent-border",
  "--caro-status-brand-bg",
  "--caro-status-brand-fg",
  "--caro-status-brand-border",
  "--caro-status-neutral-bg",
  "--caro-status-neutral-fg",
  "--caro-status-neutral-border"
];
var RESIDENT_CHROME_CONTRAST_PAIRS = [
  ["--caro-nav-fg", "--caro-nav-bg", 4.5],
  ["--caro-nav-fg-muted", "--caro-nav-bg", 4.5],
  ["--caro-nav-fg", "--caro-call-bg", 4.5],
  ["--caro-text-strong", "--caro-surface-muted", 4.5],
  ["--caro-text-strong", "--caro-surface-subtle", 4.5],
  ["--caro-accent-fg", "--caro-accent-soft", 4.5],
  ["--caro-primary-soft-fg", "--caro-primary-soft", 4.5],
  ["--caro-status-available-fg", "--caro-status-available-bg", 4.5],
  ["--caro-status-rollout-fg", "--caro-status-rollout-bg", 4.5],
  ["--caro-status-urgent-fg", "--caro-status-urgent-bg", 4.5],
  ["--caro-status-brand-fg", "--caro-status-brand-bg", 4.5],
  ["--caro-status-neutral-fg", "--caro-status-neutral-bg", 4.5],
  ["--caro-text-subtle", "--caro-surface-subtle", 3],
  ["--caro-text-strong", "--caro-paper", 4.5],
  ["--caro-nav-fg", "--caro-inverse-bg", 4.5]
];
var RESIDENT_THEME_CSS_VARS = {
  primary: "--caro-primary",
  primaryDark: "--caro-primary-dark",
  secondary: "--caro-secondary",
  bg: "--caro-bg",
  surface: "--caro-card-bg",
  border: "--caro-border",
  text: "--caro-text",
  textMuted: "--caro-text-light"
};
var RESIDENT_THEME_FORBIDDEN_VARS = [
  "--caro-emergency",
  "--caro-error",
  "--caro-success",
  "--caro-warning",
  "--touch-min",
  "--touch-emergency",
  "--font-small",
  "--font-body",
  "--font-heading",
  "--font-datetime",
  "--font-button"
];
var THEME_CATALOG_KEYS = ["caro-default", "caro-editorial"];
var THEME_CATALOG_APPS = {
  "caro-default": ["resident"],
  "caro-editorial": ["portal", "resident"]
};
var E = CARO_EDITORIAL_TOKENS;
function editorialChrome(mode) {
  const t = E[mode];
  const light = mode === "light";
  const st = t.status;
  return {
    // T095 (unchanged): skeleton = divider (light) / paper (dark), controls = divider,
    // disabled bg = page bg, fg = muted text, disabled fg = divider.
    "--caro-skeleton-bg": light ? t.divider : t.background.paper,
    "--caro-control-bg": t.divider,
    "--caro-control-bg-disabled": t.background.default,
    "--caro-control-fg": t.text.secondary,
    "--caro-control-fg-disabled": t.divider,
    "--caro-paper": t.background[light ? "subtle" : "paper"],
    // Light pages are background.neutral now, so the inner surfaces step one shade deeper.
    "--caro-surface-muted": light ? t.grey[300] : t.background.neutral,
    "--caro-surface-subtle": light ? t.background.neutral : t.background.subtle,
    "--caro-text-strong": t.text.tertiary,
    // On the warmer light page text.disabled is 2.8:1; hints use the muted text colour there.
    "--caro-text-subtle": light ? t.text.secondary : t.text.disabled,
    "--caro-divider": t.divider,
    "--caro-divider-strong": light ? t.grey[500] : st.neutral.border,
    "--caro-on-primary": t.primary.contrastText,
    "--caro-card-shadow": E.customShadows.card,
    "--caro-raised-shadow": E.customShadows.dropdown,
    "--caro-backdrop": "rgba(42, 15, 28, 0.55)",
    // #2A0F1C, the deep end of gradients.inverse
    "--caro-inverse-bg": t.background.inverse,
    "--caro-accent": t.secondary.main,
    "--caro-accent-soft": st.rollout.bg,
    "--caro-accent-fg": st.rollout.fg,
    "--caro-primary-soft": st.brand.bg,
    "--caro-primary-soft-fg": st.brand.fg,
    "--caro-title-rule": `inset 0 -3px 0 ${t.secondary.main}`,
    "--caro-card-accent": t.secondary.main,
    "--caro-nav-bg": t.sidebar.bg,
    "--caro-nav-fg": t.sidebar.fg,
    "--caro-nav-fg-muted": t.sidebar.fgMuted,
    "--caro-nav-border": t.sidebar.divider,
    "--caro-header-band": `0 -16px 0 24px ${t.sidebar.bg}`,
    // Home is an aubergine composition in both modes: near-solid aubergine tiles with a gold edge on an
    // aubergine scrim (gradients.inverse colours), so it reads the same over the stock image and
    // over resident photos.
    "--caro-home-tile-tint": `rgba(${E.shadowInk}, 0.86)`,
    // aubergine #3E1729, near-solid
    "--caro-home-tile-border": "2px solid rgba(218, 160, 61, 0.85)",
    // gold #DAA03D
    "--caro-home-scrim": "radial-gradient(ellipse at 50% 40%, rgba(106, 47, 75, 0.86) 0%, rgba(62, 23, 41, 0.93) 62%, rgba(42, 15, 28, 0.97) 100%)",
    "--caro-home-scrim-photo": "linear-gradient(180deg, rgba(62, 23, 41, 0.52) 0%, rgba(62, 23, 41, 0.62) 40%, rgba(42, 15, 28, 0.80) 100%)",
    "--caro-home-caption-bg": "rgba(62, 23, 41, 0.78)",
    "--caro-standby-bg-dark": "radial-gradient(ellipse at 50% 45%, #3E1729 0%, #22101A 55%, #10070C 100%)",
    // inverse, dimmed
    "--caro-standby-bg-light": `radial-gradient(ellipse at 50% 45%, ${E.light.background.subtle} 0%, ${E.light.background.neutral} 60%, ${E.light.divider} 100%)`,
    "--caro-call-bg": t.background.inverse,
    "--caro-font-system": "system-ui, -apple-system, sans-serif",
    "--caro-status-available-bg": st.available.bg,
    "--caro-status-available-fg": st.available.fg,
    "--caro-status-available-border": st.available.border,
    "--caro-status-rollout-bg": st.rollout.bg,
    "--caro-status-rollout-fg": st.rollout.fg,
    "--caro-status-rollout-border": st.rollout.border,
    "--caro-status-urgent-bg": st.urgent.bg,
    "--caro-status-urgent-fg": st.urgent.fg,
    "--caro-status-urgent-border": st.urgent.border,
    "--caro-status-brand-bg": st.brand.bg,
    "--caro-status-brand-fg": st.brand.fg,
    "--caro-status-brand-border": st.brand.border,
    "--caro-status-neutral-bg": st.neutral.bg,
    "--caro-status-neutral-fg": st.neutral.fg,
    "--caro-status-neutral-border": st.neutral.border
  };
}
var THEME_CATALOG = {
  // Today's resident look, value for value (packages/resident-app/src/styles/global.css).
  "caro-default": {
    key: "caro-default",
    name: "CARO standaard",
    builtIn: true,
    apps: THEME_CATALOG_APPS["caro-default"],
    light: {
      primary: "#9A3F69",
      primaryDark: "#C97AA0",
      secondary: "#2D3748",
      bg: "#F7FAFC",
      surface: "#FFFFFF",
      border: "#E2E8F0",
      text: "#1A202C",
      textMuted: "#718096"
    },
    // [data-theme="dark"]: primary paints its `--caro-primary-dark` fallback; secondary is not
    // overridden in dark today.
    dark: {
      primary: "#C97AA0",
      primaryDark: "#C97AA0",
      secondary: "#2D3748",
      bg: "#1A202C",
      surface: "#2D3748",
      border: "#4A5568",
      text: "#F7FAFC",
      textMuted: "#A0AEC0"
    },
    fonts: { body: "system" }
  },
  // Resident variant of Caro Editorial (plan §7.9.1, LD-19): colours + Nunito; Newsreader only for
  // page titles and the clock, subject to the 800×480 kiosk check (T108).
  "caro-editorial": {
    key: "caro-editorial",
    name: "Caro Editorial",
    builtIn: true,
    apps: THEME_CATALOG_APPS["caro-editorial"],
    light: {
      primary: E.light.primary.main,
      primaryDark: E.dark.primary.main,
      secondary: E.light.text.primary,
      // Resident light (owner 2026-10-01, resident audit): the warmer `background.neutral` page with
      // cream `background.subtle` cards — the portal keeps default/paper. Both AA (contrast tests).
      bg: E.light.background.neutral,
      surface: E.light.background.subtle,
      border: E.light.divider,
      text: E.light.text.primary,
      textMuted: E.light.text.secondary
    },
    dark: {
      primary: E.dark.primary.main,
      primaryDark: E.dark.primary.main,
      secondary: E.dark.text.primary,
      bg: E.dark.background.default,
      surface: E.dark.background.paper,
      border: E.dark.divider,
      text: E.dark.text.primary,
      textMuted: E.dark.text.secondary
    },
    fonts: { body: "nunito", display: "newsreader", mono: "jetbrains-mono" },
    radius: { sm: E.radius.sm, md: E.radius.md, lg: E.radius.lg },
    residentChrome: { light: editorialChrome("light"), dark: editorialChrome("dark") }
  }
};
function resolveThemeKey(chain, catalog = THEME_CATALOG) {
  for (const key of chain) {
    if (typeof key === "string" && key !== "" && Object.prototype.hasOwnProperty.call(catalog, key)) {
      return key;
    }
  }
  return null;
}

// src/theme/chipStatus.ts
var MUI_CHIP_COLORS = [
  "default",
  "primary",
  "secondary",
  "success",
  "warning",
  "error",
  "info"
];
var CHIP_COLOR_TO_STATUS = {
  success: "available",
  warning: "rollout",
  secondary: "rollout",
  error: "urgent",
  info: "brand",
  primary: "brand",
  default: "neutral"
};

// src/theme/index.ts
var DEFAULT_PRIMARY = "#9A3F69";
var DEFAULT_SECONDARY = "#2D3748";
var DEFAULT_FONT_FAMILY = "system-ui, -apple-system, sans-serif";
var DEFAULT_THEME = {
  primaryColor: DEFAULT_PRIMARY,
  // Pre-computed for the default plum — round-trips identically through
  // `deriveDarkPrimary(DEFAULT_PRIMARY)`. Kept inline so the default theme
  // object is a plain literal (no module-init side effects on import).
  darkPrimary: deriveDarkPrimary(DEFAULT_PRIMARY),
  secondaryColor: DEFAULT_SECONDARY,
  fontFamily: DEFAULT_FONT_FAMILY,
  fontSize: {
    small: 12,
    body: 18,
    heading: 24,
    dateTime: 32,
    button: 18
  },
  touchTargets: {
    minimum: 48,
    emergency: 80
  }
};
function createTheme(branding) {
  const primary = branding?.primaryColor || DEFAULT_PRIMARY;
  const secondary = branding?.secondaryColor || DEFAULT_SECONDARY;
  const fontFamily = branding?.fontFamily || DEFAULT_FONT_FAMILY;
  return {
    ...DEFAULT_THEME,
    primaryColor: primary,
    darkPrimary: deriveDarkPrimary(primary),
    secondaryColor: secondary,
    fontFamily,
    logo: branding?.logo
  };
}
var GLASS = {
  /** Lichte modus: melkwit, zodat donkere tekst leesbaar blijft. */
  background: "rgba(255, 255, 255, 0.72)",
  backgroundDark: "rgba(22, 22, 26, 0.62)",
  /** De randlijn IS het glas: zonder die lichte kier zweeft een vlak in plaats van te liggen. */
  border: "rgba(255, 255, 255, 0.55)",
  borderDark: "rgba(255, 255, 255, 0.10)",
  shadow: "0 8px 32px rgba(16, 16, 18, 0.12)",
  shadowDark: "0 8px 32px rgba(0, 0, 0, 0.45)",
  filter: "saturate(180%) blur(14px)",
  // ── EEN TWEEDE, LICHTERE VARIANT VOOR BEDIENING OVER BEELD ───────────────
  //
  // De ronde pijlknoppen op de presentatiepagina's liggen op een donkere afbeelding, niet
  // op een pagina. Daar hoort glas dat het beeld nauwelijks aantast: je moet er doorheen
  // kunnen kijken, want wat eronder ligt is de inhoud. Ze in `glassSurface()` persen zou
  // ze in melkglas veranderen en precies verbergen waar ze overheen liggen.
  controlBackground: "rgba(255, 255, 255, 0.14)",
  controlBackgroundHover: "rgba(255, 255, 255, 0.26)",
  controlBorder: "rgba(255, 255, 255, 0.22)",
  // ── EN EEN DICHTERE VARIANT VOOR INHOUD ──────────────────────────────────
  //
  // Een zwevende balk mag doorzichtig zijn; een dialoog met een formulier of een tabel
  // niet. Dichte tekst op te veel doorschijn is moeilijk te lezen, en dat weegt zwaarder
  // dan het effect. Vandaar één trap dichter voor oppervlakken die inhoud dragen.
  backgroundStrong: "rgba(255, 255, 255, 0.88)",
  backgroundStrongDark: "rgba(26, 26, 31, 0.86)"
};
function glassSurface(opts = {}) {
  return {
    backgroundColor: opts.strong ? "var(--caro-glass-bg-strong)" : "var(--caro-glass-bg)",
    backdropFilter: "var(--caro-glass-filter)",
    WebkitBackdropFilter: "var(--caro-glass-filter)",
    border: "1px solid var(--caro-glass-border)",
    boxShadow: "var(--caro-glass-shadow)"
  };
}
function glassControl() {
  return {
    backgroundColor: "var(--caro-glass-control-bg)",
    backdropFilter: "var(--caro-glass-filter)",
    WebkitBackdropFilter: "var(--caro-glass-filter)",
    border: "1px solid var(--caro-glass-control-border)",
    transition: "background-color 160ms ease-out",
    "&:hover": { backgroundColor: "var(--caro-glass-control-bg-hover)" }
  };
}
function themeToCssVars(theme) {
  const vars = {
    "--caro-primary": theme.primaryColor,
    "--caro-primary-dark": theme.darkPrimary,
    "--caro-secondary": theme.secondaryColor,
    "--caro-font": theme.fontFamily,
    "--font-small": `${theme.fontSize.small}px`,
    "--font-body": `${theme.fontSize.body}px`,
    "--font-heading": `${theme.fontSize.heading}px`,
    "--font-datetime": `${theme.fontSize.dateTime}px`,
    "--font-button": `${theme.fontSize.button}px`,
    "--touch-min": `${theme.touchTargets.minimum}px`,
    "--touch-emergency": `${theme.touchTargets.emergency}px`,
    // Glas. De `-dark`-varianten rijden mee zodat een donkere modus de basisvariabele
    // erheen kan wijzen — hetzelfde patroon als `--caro-primary-dark` hierboven, en om
    // dezelfde reden: één omschakeling in CSS in plaats van een tweede tokenset in JS.
    "--caro-glass-bg": GLASS.background,
    "--caro-glass-bg-dark": GLASS.backgroundDark,
    "--caro-glass-border": GLASS.border,
    "--caro-glass-border-dark": GLASS.borderDark,
    "--caro-glass-shadow": GLASS.shadow,
    "--caro-glass-shadow-dark": GLASS.shadowDark,
    "--caro-glass-filter": GLASS.filter,
    "--caro-glass-control-bg": GLASS.controlBackground,
    "--caro-glass-control-bg-hover": GLASS.controlBackgroundHover,
    "--caro-glass-control-border": GLASS.controlBorder,
    "--caro-glass-bg-strong": GLASS.backgroundStrong,
    "--caro-glass-bg-strong-dark": GLASS.backgroundStrongDark
  };
  if (theme.logo) {
    vars["--caro-logo-url"] = `url(${theme.logo})`;
  }
  return vars;
}
function applyThemeCssVars(theme, target) {
  if (typeof document === "undefined" && !target) return {};
  const el = target ?? document.documentElement;
  const vars = themeToCssVars(theme);
  for (const [name, value] of Object.entries(vars)) {
    el.style.setProperty(name, value);
  }
  return vars;
}

export { BUNDLED_FONTS, CARO_EDITORIAL_CONTRAST_PAIRS, CARO_EDITORIAL_TOKENS, CHIP_COLOR_TO_STATUS, DARK_PRIMARY_FALLBACK, DEFAULT_THEME, GLASS, MUI_CHIP_COLORS, RESIDENT_CHROME_CONTRAST_PAIRS, RESIDENT_THEME_CHROME_VARS, RESIDENT_THEME_CSS_VARS, RESIDENT_THEME_FORBIDDEN_VARS, THEME_CATALOG, THEME_CATALOG_APPS, THEME_CATALOG_KEYS, applyThemeCssVars, contrastRatio, createTheme, deriveDarkPrimary, glassControl, glassSurface, hexToHsl, hslToHex, relativeLuminance, resolveThemeKey, themeToCssVars };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map